type FpComponents = Record<string, unknown>;

export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`);
  return `{${entries.join(',')}}`;
}

export async function sha256Hex(input: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function collectCanvas(): string | null {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 240;
    canvas.height = 60;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(120, 5, 60, 20);
    ctx.fillStyle = '#069';
    ctx.font = '13px Arial';
    ctx.fillText('SHOT fp 🎯', 2, 20);
    ctx.strokeStyle = 'rgba(120, 40, 200, 0.6)';
    ctx.beginPath();
    ctx.arc(60, 30, 25, 0, Math.PI * 1.5);
    ctx.stroke();
    return canvas.toDataURL();
  } catch {
    return null;
  }
}

function collectWebgl(): string | null {
  try {
    const gl = document.createElement('canvas').getContext('webgl');
    if (!gl) return null;
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    if (!info) return null;
    return `${gl.getParameter(info.UNMASKED_VENDOR_WEBGL)}|${gl.getParameter(info.UNMASKED_RENDERER_WEBGL)}`;
  } catch {
    return null;
  }
}

async function collectAudio(): Promise<string | null> {
  try {
    const ctx = new OfflineAudioContext(1, 44100, 44100);
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = 10000;
    const comp = ctx.createDynamicsCompressor();
    osc.connect(comp);
    comp.connect(ctx.destination);
    osc.start(0);
    const rendered = await ctx.startRendering();
    const data = rendered.getChannelData(0).subarray(4500, 5000);
    let sum = 0;
    for (let i = 0; i < data.length; i++) sum += Math.abs(data[i]);
    return sum.toFixed(6);
  } catch {
    return null;
  }
}

function collectWebglDraw(): string | null {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true });
    if (!gl) return null;
    const vs = gl.createShader(gl.VERTEX_SHADER);
    const fs = gl.createShader(gl.FRAGMENT_SHADER);
    const prog = gl.createProgram();
    if (!vs || !fs || !prog) return null;
    gl.shaderSource(vs, 'attribute vec2 p;varying vec2 v;void main(){v=p;gl_Position=vec4(p,0.,1.);}');
    gl.compileShader(vs);
    gl.shaderSource(
      fs,
      'precision mediump float;varying vec2 v;void main(){gl_FragColor=vec4(abs(sin(v.x*37.)),abs(cos(v.y*57.)),abs(sin((v.x+v.y)*17.)),1.);}',
    );
    gl.compileShader(fs);
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    gl.useProgram(prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    const pixels = new Uint8Array(64 * 64 * 4);
    gl.readPixels(0, 0, 64, 64, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    let acc = '';
    for (let i = 0; i < pixels.length; i += 997) acc += pixels[i].toString(16);
    return acc;
  } catch {
    return null;
  }
}

const TEST_FONTS = [
  'Arial Narrow', 'Comic Sans MS', 'Courier New', 'Georgia', 'Impact', 'Tahoma',
  'Times New Roman', 'Trebuchet MS', 'Verdana', 'Helvetica Neue', 'Segoe UI',
  'Roboto', 'SF Pro Text', 'Ubuntu', 'Noto Sans',
];
const BASE_FONTS = ['monospace', 'serif', 'sans-serif'] as const;

function collectFonts(): string[] {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return [];
    const measure = (font: string) => {
      ctx.font = `72px ${font}`;
      return ctx.measureText('mmMwWLliI0O&1').width;
    };
    const base = Object.fromEntries(BASE_FONTS.map((b) => [b, measure(b)]));
    return TEST_FONTS.filter((f) => BASE_FONTS.some((b) => measure(`'${f}',${b}`) !== base[b]));
  } catch {
    return [];
  }
}

async function collectComponents(): Promise<FpComponents> {
  const nav = navigator as Navigator & { deviceMemory?: number; userAgentData?: { platform?: string } };
  return {
    canvas: collectCanvas(),
    webgl: collectWebgl(),
    webglDraw: collectWebglDraw(),
    audio: await collectAudio(),
    fonts: collectFonts(),
    screen: [window.screen.width, window.screen.height, window.screen.colorDepth, window.devicePixelRatio],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    languages: [...(nav.languages ?? [])],
    hardwareConcurrency: nav.hardwareConcurrency ?? null,
    deviceMemory: nav.deviceMemory ?? null,
    platform: nav.userAgentData?.platform ?? nav.platform ?? null,
    maxTouchPoints: nav.maxTouchPoints ?? 0,
  };
}

export interface DeviceFingerprint {
  fpHash: string;
  components: FpComponents;
}

let cached: Promise<DeviceFingerprint | null> | null = null;

export function getDeviceFingerprint(): Promise<DeviceFingerprint | null> {
  if (typeof window === 'undefined' || !globalThis.crypto?.subtle) return Promise.resolve(null);
  cached ??= (async () => {
    try {
      const components = await collectComponents();
      return { fpHash: await sha256Hex(stableStringify(components)), components };
    } catch {
      return null;
    }
  })();
  return cached;
}
